/**
 * ZIvapp routes.
 *
 * Every screen is a placeholder right now. The structure is final; the look
 * is not. See docs/references/ for where the design images go.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, AuthProvider, ChatProvider, CallProvider, useAuth } from './context/index.js'
import AppShell from './components/layout/AppShell.jsx'

import Splash from './pages/Splash.jsx'
import LinkDevice from './pages/LinkDevice.jsx'
import Onboarding from './pages/Onboarding.jsx'
import ProfileSetup from './pages/ProfileSetup.jsx'
import Permissions from './pages/Permissions.jsx'
import Login from './pages/Login.jsx'
import VerifyOtp from './pages/VerifyOtp.jsx'
import ChatsLayout from './pages/ChatsLayout.jsx'
import NoChatSelected from './pages/NoChatSelected.jsx'
import ChatRoom from './pages/ChatRoom.jsx'
import Updates from './pages/Updates.jsx'
import NewStatus from './pages/NewStatus.jsx'
import ExploreChannels from './pages/ExploreChannels.jsx'
import NewChannel from './pages/NewChannel.jsx'
import Channel from './pages/Channel.jsx'
import Communities from './pages/Communities.jsx'
import NewCommunity from './pages/NewCommunity.jsx'
import CommunityInfo from './pages/CommunityInfo.jsx'
import Calls from './pages/Calls.jsx'
import Contacts from './pages/Contacts.jsx'
import NewContact from './pages/NewContact.jsx'
import NewGroup from './pages/NewGroup.jsx'
import GroupInfo from './pages/GroupInfo.jsx'
import Profile from './pages/Profile.jsx'
import Settings from './pages/Settings.jsx'
import NotFound from './pages/NotFound.jsx'

/**
 * What "/" shows depends on who is here.
 *
 * A signed-out visitor lands on the device-linking screen, not the welcome
 * screen. On the web ZIvapp is a companion to the phone: the account lives
 * there and the browser borrows it. Account creation sits one click away
 * behind "Get started", which is what /onboarding is for.
 *
 * Splash while the auth state resolves — without it the link screen flashes
 * for a moment on every load for someone already signed in.
 */
/*
 * ...except on a phone, where the link screen is a dead end: there is no
 * second device to scan the QR code with, so it asks people to scan it with
 * the phone they are already holding. Small screens go straight to
 * onboarding and sign in on the spot (owner, 2026-11-03).
 */
const SMALL_SCREEN = '(max-width: 900px)'

export function signedOutLanding () {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return '/link'
  }
  return window.matchMedia(SMALL_SCREEN).matches ? '/onboarding' : '/link'
}

function Entry() {
  const { isLoading, isSignedIn } = useAuth()
  if (isLoading) return <Splash />
  return <Navigate to={isSignedIn ? '/chats' : signedOutLanding()} replace />
}

/**
 * Keeps signed-out visitors out of the app.
 *
 * Only "/" checked auth before, so signing out from Settings left you
 * sitting on Settings inside a signed-out app, and typing /chats straight
 * into the address bar rendered the shell around nothing. The guard wraps
 * the whole shell instead of each screen.
 */
function RequireAuth({ children }) {
  const { isLoading, isSignedIn } = useAuth()
  if (isLoading) return <Splash />
  if (!isSignedIn) return <Navigate to={signedOutLanding()} replace />
  return children
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ChatProvider>
          <CallProvider>
            <BrowserRouter>
              <Routes>
                {/* Auth flow sits outside the shell - no nav bar during sign-in */}
                <Route path="/" element={<Entry />} />
                <Route path="/splash" element={<Splash />} />
                <Route path="/link" element={<LinkDevice />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/login" element={<Login />} />
                <Route path="/verify" element={<VerifyOtp />} />
                <Route path="/profile-setup" element={<ProfileSetup />} />
                <Route path="/permissions" element={<Permissions />} />

                {/*
                  The status composer is OUTSIDE the shell, on purpose.

                  A status is composed full-screen — the coloured canvas IS
                  the status, so the nav rail sitting beside it both shrinks
                  the canvas and misrepresents what will be posted. Still
                  behind RequireAuth; only the chrome is dropped.
                */}
                <Route
                  path="/status/new/:kind"
                  element={<RequireAuth><NewStatus /></RequireAuth>}
                />

                <Route element={<RequireAuth><AppShell /></RequireAuth>}>
                  {/* A layout route, so the list stays mounted - and keeps
                      its scroll position and subscription - while you move
                      between conversations. */}
                  <Route path="/chats" element={<ChatsLayout />}>
                    <Route index element={<NoChatSelected />} />
                    <Route path=":chatId" element={<ChatRoom />} />
                  </Route>
                  <Route path="/updates" element={<Updates />} />

                  {/* /channels/new before /channels/:channelId, or "new" is
                      read as an id and the create screen is unreachable. */}
                  <Route path="/channels" element={<ExploreChannels />} />
                  <Route path="/channels/new" element={<NewChannel />} />
                  <Route path="/channels/:channelId" element={<Channel />} />
                  <Route path="/communities" element={<Communities />} />

                  {/* /new before /:communityId, or "new" is read as an id. */}
                  <Route path="/communities/new" element={<NewCommunity />} />
                  <Route path="/communities/:communityId" element={<CommunityInfo />} />
                  <Route path="/calls" element={<Calls />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/contacts/new" element={<NewContact />} />
                  <Route path="/groups/new" element={<NewGroup />} />
                  <Route path="/groups/:chatId" element={<GroupInfo />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </CallProvider>
        </ChatProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
